import { Box, Container, Typography, Grid, Card, CardContent, Chip } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SyncIcon from '@mui/icons-material/Sync';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

type Status = 'done' | 'in-progress' | 'planned';

interface RoadmapItem {
    title: string;
    description: string;
    status: Status;
}

const items: RoadmapItem[] = [
    { status: 'done', title: 'Open Badges 2.0+ Compliance', description: 'All badges conform to the IMS Open Badges 2.0+ standard — fully portable and independently verifiable.' },
    { status: 'done', title: 'AI Badge Assist', description: 'AI Assist provides suggestions for badge titles, descriptions, keywords, and assessment questions during badge creation.' },
    { status: 'done', title: 'RESTful API', description: 'A RESTful API for creating and issuing badges programmatically and bringing badge data into external systems.' },
    { status: 'in-progress', title: 'Multi-Language Support', description: 'TC Everywhere will be available in multiple languages. Badge creators will declare the badge language, and manually created badges can have multiple language elements.' },
    { status: 'in-progress', title: 'Personalized Public Profile Page', description: 'Users will be able to customize the look and feel of their public profile pages to better reflect their personality and style.' },
    { status: 'planned', title: 'Branding Options for Issuer Pages', description: "Issuers will gain the ability to brand their own pages in line with their organization's identity. Custom-friendly URLs will also be supported." },
    { status: 'planned', title: 'Mobile App for Android and iOS', description: 'A dedicated mobile app that brings badge discovery and earning to your smartphone, mirroring all features of the browser extension.' },
    { status: 'planned', title: 'Safari Extension Support', description: 'Extending our browser extension support to Safari, so Mac and iOS users can earn badges without switching browsers.' },
    { status: 'planned', title: 'Blockchain Support', description: 'For badges too important to trust to any single platform, TC Everywhere will add support for the public blockchain — ensuring a permanent, decentralized record.' },
    { status: 'planned', title: 'Attendance Badges via QR Code and Geofencing', description: 'Earn badges for attending real-world events, validated through QR codes and geofencing via the mobile app.' },
    { status: 'planned', title: 'Localized Augmented Reality Badges', description: 'With the mobile app, users will have the power to find and earn badges in the physical world using augmented reality.' },
    { status: 'planned', title: 'Export CLR 3.0 with Advanced Sharing', description: 'The personal portfolio will support advanced sharing controls — set detailed rules on who can view your badges and what parts are visible.' },
    { status: 'planned', title: 'New Question Types', description: 'Expanding assessment tools with Matching, Fill in the Blank, and Hot Spot question types — supported in both the extension and mobile app.' },
    { status: 'planned', title: 'JavaScript Player for Any Browser', description: 'A JavaScript player that brings the full power of TC Everywhere badge-earning to any website, supporting all browsers and mobile devices.' },
];

const statusConfig: Record<Status, { label: string; color: 'success' | 'warning' | 'default'; Icon: React.ComponentType<{ sx?: object }> }> = {
    'done':        { label: 'Released',    color: 'success', Icon: CheckCircleIcon },
    'in-progress': { label: 'In Progress', color: 'warning', Icon: SyncIcon },
    'planned':     { label: 'Planned',     color: 'default', Icon: AccessTimeIcon },
};

const iconColor: Record<Status, string> = {
    'done':        'success.main',
    'in-progress': 'warning.main',
    'planned':     'text.secondary',
};

function Section({ title, items, status }: { title: string; items: RoadmapItem[]; status: Status }) {
    const { Icon, color } = statusConfig[status];
    return (
        <Box sx={{ mb: 6 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Icon sx={{ color: iconColor[status], fontSize: 28 }} />
                <Typography variant="h5" fontWeight={700}>{title}</Typography>
                <Chip label={items.length} color={color} size="small" />
            </Box>
            <Grid container spacing={2}>
                {items.map(item => (
                    <Grid item xs={12} md={6} key={item.title}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent sx={{ display: 'flex', gap: 2 }}>
                                <Icon sx={{ color: iconColor[status], flexShrink: 0, mt: 0.25 }} />
                                <Box>
                                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                        {item.title}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {item.description}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}

export default function RoadmapPage() {
    const done       = items.filter(i => i.status === 'done');
    const inProgress = items.filter(i => i.status === 'in-progress');
    const planned    = items.filter(i => i.status === 'planned');

    return (
        <>
            <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 8 }}>
                <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
                    <Typography variant="h2" fontWeight={700} gutterBottom>
                        TC Everywhere Road Map
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.85 }}>
                        Here's what we've shipped, what we're building, and what's coming next.
                    </Typography>
                </Container>
            </Box>

            <Box sx={{ py: 8 }}>
                <Container maxWidth="lg">
                    <Section title="Released"    items={done}       status="done" />
                    <Section title="In Progress" items={inProgress} status="in-progress" />
                    <Section title="Planned"     items={planned}    status="planned" />
                </Container>
            </Box>
        </>
    );
}
